import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError } from "@/components/shared/helpers";

export default function AdminSupplements({ properties }) {
  const [activeSection, setActiveSection] = useState("surcharge");
  const [surcharges, setSurcharges] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [surchargeForm, setSurchargeForm] = useState({ property_id: "", amount: "", surcharge_type: "", other_description: "", surcharge_date: new Date().toISOString().slice(0, 10) });
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [interventionForm, setInterventionForm] = useState({ property_id: "", intervention_date: "", description: "", amount: "" });
  const [submitting, setSubmitting] = useState(false);

  const surchargeTypes = [
    { value: "poubelle", label: "Poubelle non sortie" },
    { value: "vaisselle", label: "Vaisselle sale laissée" },
    { value: "linge_pas_defait", label: "Linge pas défait" },
    { value: "cheminee_pas_videe", label: "Cheminée pas vidée" },
    { value: "autre", label: "Autre" }
  ];

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [surRes, intRes] = await Promise.all([api.get("/admin/surcharges"), api.get("/admin/interventions")]);
      setSurcharges(surRes.data || []);
      setInterventions(intRes.data || []);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
    setLoading(false);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (uploadedPhotos.length + files.length > 5) {
      toast({ title: "Limite atteinte", description: "Maximum 5 photos autorisées", variant: "destructive" });
      return;
    }
    setUploading(true);
    const formData = new FormData();
    files.forEach(file => formData.append("files", file));
    try {
      const res = await api.post("/admin/photos/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedPhotos(prev => [...prev, ...res.data.photos]);
      toast({ title: "Photos uploadées", description: `${res.data.photos.length} photo(s) ajoutée(s)` });
    } catch (e) {
      toast({ title: "Erreur upload", description: formatError(e), variant: "destructive" });
    }
    setUploading(false);
  };

  const removePhoto = (filename) => { setUploadedPhotos(prev => prev.filter(p => p !== filename)); };

  const submitSurcharge = async () => {
    if (!surchargeForm.property_id || !surchargeForm.amount || !surchargeForm.surcharge_type) {
      toast({ title: "Champs manquants", description: "Veuillez remplir tous les champs obligatoires", variant: "destructive" });
      return;
    }
    if (surchargeForm.surcharge_type === "autre" && !surchargeForm.other_description) {
      toast({ title: "Description requise", description: "Veuillez décrire le problème", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const formData = new FormData();
    formData.append("property_id", surchargeForm.property_id);
    formData.append("amount", surchargeForm.amount);
    formData.append("surcharge_type", surchargeForm.surcharge_type);
    if (surchargeForm.other_description) formData.append("other_description", surchargeForm.other_description);
    if (surchargeForm.surcharge_date) formData.append("surcharge_date", surchargeForm.surcharge_date);
    formData.append("photos", uploadedPhotos.join(","));
    try {
      await api.post("/admin/surcharges", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "Surcoût enregistré", description: "Email envoyé au propriétaire" });
      setSurchargeForm({ property_id: "", amount: "", surcharge_type: "", other_description: "", surcharge_date: new Date().toISOString().slice(0, 10) });
      setUploadedPhotos([]);
      loadData();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
    setSubmitting(false);
  };

  const submitIntervention = async () => {
    if (!interventionForm.property_id || !interventionForm.intervention_date || !interventionForm.description || !interventionForm.amount) {
      toast({ title: "Champs manquants", description: "Veuillez remplir tous les champs", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const formData = new FormData();
    formData.append("property_id", interventionForm.property_id);
    formData.append("intervention_date", interventionForm.intervention_date);
    formData.append("description", interventionForm.description);
    formData.append("amount", interventionForm.amount);
    try {
      await api.post("/admin/interventions", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "Intervention enregistrée", description: "Email envoyé au propriétaire" });
      setInterventionForm({ property_id: "", intervention_date: "", description: "", amount: "" });
      loadData();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="admin-supplements" className="space-y-4 sm:space-y-6 w-full">
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-white">Supplément</h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">Surcoûts ménage et interventions</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={activeSection === "surcharge" ? "default" : "outline"} className={`text-xs sm:text-sm ${activeSection === "surcharge" ? "bg-violet-500 text-white hover:bg-violet-400" : "border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"}`} onClick={() => setActiveSection("surcharge")}>🧹 Surcoût</Button>
          <Button size="sm" variant={activeSection === "intervention" ? "default" : "outline"} className={`text-xs sm:text-sm ${activeSection === "intervention" ? "bg-amber-500 text-white hover:bg-amber-400" : "border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"}`} onClick={() => setActiveSection("intervention")}>🔧 Intervention</Button>
        </div>
      </div>

      {activeSection === "surcharge" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-violet-500/20 bg-violet-500/5">
            <CardHeader><CardTitle className="text-lg text-violet-300">🧹 Nouveau surcoût ménage</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-200">Appartement *</Label>
                <select className="w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-slate-100" value={surchargeForm.property_id} onChange={(e) => setSurchargeForm(prev => ({ ...prev, property_id: e.target.value }))}>
                  <option value="">Sélectionner...</option>
                  {properties.map(p => (<option key={p.id} value={p.id}>{p.short_name}</option>))}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Date du constat *</Label>
                <Input data-testid="admin-surcharge-date-input" type="date" className="border-white/10 bg-white/[0.08]" value={surchargeForm.surcharge_date} onChange={(e) => setSurchargeForm(prev => ({ ...prev, surcharge_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Type de problème *</Label>
                <select className="w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-slate-100" value={surchargeForm.surcharge_type} onChange={(e) => setSurchargeForm(prev => ({ ...prev, surcharge_type: e.target.value }))}>
                  <option value="">Sélectionner...</option>
                  {surchargeTypes.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
                </select>
              </div>
              {surchargeForm.surcharge_type === "autre" && (
                <div className="space-y-2">
                  <Label className="text-slate-200">Description du problème *</Label>
                  <Input className="border-white/10 bg-white/[0.08]" placeholder="Décrivez le problème..." value={surchargeForm.other_description} onChange={(e) => setSurchargeForm(prev => ({ ...prev, other_description: e.target.value }))} />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-200">Montant (€) *</Label>
                <Input type="number" min="0" step="0.01" className="border-white/10 bg-white/[0.08]" placeholder="0.00" value={surchargeForm.amount} onChange={(e) => setSurchargeForm(prev => ({ ...prev, amount: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Photos (max 5)</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {uploadedPhotos.map((photo, idx) => (
                    <div key={photo} className="relative group">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/photos/${photo}`} alt={`Photo ${idx + 1}`} className="h-20 w-20 object-cover rounded-lg border border-white/10" />
                      <button type="button" onClick={() => removePhoto(photo)} className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                    </div>
                  ))}
                </div>
                <Input type="file" accept="image/*" multiple capture="environment" className="border-white/10 bg-white/[0.08]" onChange={handlePhotoUpload} disabled={uploading || uploadedPhotos.length >= 5} />
                {uploading && <p className="text-xs text-slate-400">Upload en cours...</p>}
              </div>
              <Button className="w-full bg-violet-500 text-white hover:bg-violet-400" onClick={submitSurcharge} disabled={submitting}>{submitting ? "Enregistrement..." : "✅ Enregistrer et notifier"}</Button>
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-white/[0.08]">
            <CardHeader><CardTitle className="text-lg text-white">📋 Historique surcoûts</CardTitle></CardHeader>
            <CardContent>
              {loading ? (<p className="text-slate-400 text-center py-4">Chargement...</p>) : surcharges.length === 0 ? (<p className="text-slate-400 text-center py-4">Aucun surcoût enregistré</p>) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {surcharges.map((s, idx) => (
                    <div key={s.id || idx} className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-white">{s.property_name}</div>
                          <div className="text-sm text-violet-300">{s.surcharge_type_label}</div>
                          {s.other_description && <div className="text-xs text-slate-400 mt-1">{s.other_description}</div>}
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-emerald-400">{s.amount?.toFixed(2)} €</div>
                          <div className="text-xs text-slate-500">{formatDateEU(s.created_at?.split("T")[0])}</div>
                        </div>
                      </div>
                      {s.photos && s.photos.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {s.photos.map((photo, pidx) => (<img key={photo} src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/photos/${photo}`} alt={`Photo ${pidx + 1}`} className="h-12 w-12 object-cover rounded border border-white/10" />))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeSection === "intervention" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-amber-500/20 bg-amber-500/5">
            <CardHeader><CardTitle className="text-lg text-amber-300">🔧 Nouvelle intervention</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-200">Appartement *</Label>
                <select className="w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-slate-100" value={interventionForm.property_id} onChange={(e) => setInterventionForm(prev => ({ ...prev, property_id: e.target.value }))}>
                  <option value="">Sélectionner...</option>
                  {properties.map(p => (<option key={p.id} value={p.id}>{p.short_name}</option>))}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Date d&apos;intervention *</Label>
                <Input type="date" className="border-white/10 bg-white/[0.08]" value={interventionForm.intervention_date} onChange={(e) => setInterventionForm(prev => ({ ...prev, intervention_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Description *</Label>
                <textarea className="w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-slate-100 min-h-[80px]" placeholder="Décrivez l'intervention..." value={interventionForm.description} onChange={(e) => setInterventionForm(prev => ({ ...prev, description: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Montant (€) *</Label>
                <Input type="number" min="0" step="0.01" className="border-white/10 bg-white/[0.08]" placeholder="0.00" value={interventionForm.amount} onChange={(e) => setInterventionForm(prev => ({ ...prev, amount: e.target.value }))} />
              </div>
              <Button className="w-full bg-amber-500 text-white hover:bg-amber-400" onClick={submitIntervention} disabled={submitting}>{submitting ? "Enregistrement..." : "✅ Enregistrer et notifier"}</Button>
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-white/[0.08]">
            <CardHeader><CardTitle className="text-lg text-white">📋 Historique interventions</CardTitle></CardHeader>
            <CardContent>
              {loading ? (<p className="text-slate-400 text-center py-4">Chargement...</p>) : interventions.length === 0 ? (<p className="text-slate-400 text-center py-4">Aucune intervention enregistrée</p>) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {interventions.map((i, idx) => (
                    <div key={i.id || idx} className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-white">{i.property_name}</div>
                          <div className="text-sm text-slate-300 mt-1">{i.description}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-emerald-400">{i.amount?.toFixed(2)} €</div>
                          <div className="text-xs text-slate-500">{formatDateEU(i.intervention_date)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
